import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared-kernel/primary/shared/shadcn/ui/tooltip";
import type { AddressLookup } from "@shared-kernel/domain/types/address-lookup";
import { SUPPORTED_COUNTRY, COUNTRY_LABELS } from "@shared-kernel/domain/types/supported-country";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { Label } from "@shared-kernel/primary/shared/shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared-kernel/primary/shared/shadcn/ui/select";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { AddressAutocompleteInput } from "./address-autocomplete-input";
import { useFormContext } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";

const ALL_COUNTRIES = Object.values(SUPPORTED_COUNTRY);

const EMPTY_ADDRESS: AddressLookup = { placeId: "", description: "", sessionToken: "", invalid: false };

interface AddressBlockFields {
  postalAddress: string;
  addressComplement: string;
  country: string;
}

interface AddressBlockProps {
  fields: AddressBlockFields;
  disabled?: boolean;
  addressLabel?: string;
  addressSubtitle?: string;
  countries?: SUPPORTED_COUNTRY[];
}

export function AddressBlock({
  fields,
  disabled = false,
  addressLabel = "Adresse",
  addressSubtitle,
  countries = ALL_COUNTRIES,
}: AddressBlockProps) {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const country = watch(fields.country) as SUPPORTED_COUNTRY;
  const postalAddress = watch(fields.postalAddress) as AddressLookup | null;
  const address = postalAddress?.description || postalAddress?.placeId ? postalAddress : null;

  const handleAddressChange = (value: AddressLookup | null) => {
    setValue(fields.postalAddress, value ?? EMPTY_ADDRESS, { shouldValidate: true, shouldDirty: true });
  };

  const postalAddressPlaceIdError = getNestedError(errors, `${fields.postalAddress}.placeId`);
  const countryLabel = COUNTRY_LABELS[country];

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <div className="flex items-center gap-1.5">
          <Label className="text-sm font-medium">{addressLabel}</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger type="button" className="text-muted-foreground/70 hover:text-muted-foreground">
                <Info className="size-3.5" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                L'adresse doit être reconnue par Google. En cas de difficulté, recherchez-la sur Google Maps puis copiez-collez-la ici.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {addressSubtitle && <FormDescription>{addressSubtitle}</FormDescription>}
      </div>

      <div className="relative space-y-3 rounded-lg border border-border bg-muted/20 p-2 transition-colors has-[:focus]:border-ring/40">
        <FormField
          name={fields.country}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Pays</FormLabel>
              <Select
                value={field.value}
                onValueChange={value => {
                  field.onChange(value);
                  setValue(fields.postalAddress, EMPTY_ADDRESS, { shouldDirty: true });
                  setValue(fields.addressComplement, "", { shouldDirty: true });
                }}
                disabled={disabled}
              >
                <FormControl>
                  <SelectTrigger className={cn("h-9 w-48 bg-background", fieldState.error && "border-destructive/50")}>
                    <SelectValue>
                      {countryLabel ? (
                        <span className="flex items-center gap-2">
                          <span>{countryLabel.flag}</span>
                          <span>{countryLabel.label}</span>
                        </span>
                      ) : (
                        "Sélectionner un pays"
                      )}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {countries.map(code => {
                    const { label, flag } = COUNTRY_LABELS[code];
                    return (
                      <SelectItem key={code} value={code}>
                        <span className="flex items-center gap-2">
                          <span>{flag}</span>
                          <span>{label}</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-1">
          <Label className="text-sm font-medium">Adresse postale</Label>
          <AddressAutocompleteInput value={address} onChange={handleAddressChange} disabled={disabled} country={country} />
          {postalAddressPlaceIdError && <p className="text-sm font-medium text-destructive">{postalAddressPlaceIdError}</p>}
        </div>

        <FormField
          name={fields.addressComplement}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Complément</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ""}
                  placeholder="Bâtiment, étage, porte..."
                  disabled={disabled}
                  className={cn("h-9 bg-background", fieldState.error && "border-destructive/50")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

function getNestedError(errors: Record<string, unknown>, path: string): string | undefined {
  const parts = path.split(".");
  let current: unknown = errors;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  if (current != null && typeof current === "object" && "message" in current) {
    return (current as { message?: string }).message;
  }
  return undefined;
}
